import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Box,
  Button,
  Chip,
  Divider,
  TextField,
  CircularProgress,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import DoneIcon from '@mui/icons-material/Done';
import PrintIcon from '@mui/icons-material/Print';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PersonAddDisabledIcon from '@mui/icons-material/PersonAddDisabled';
import CloseIcon from '@mui/icons-material/Close';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import WarningIcon from '@mui/icons-material/Warning';
import ReadMemberCards from './ReadMemberCards';
import EditableText from '../Bookings/BookingDetailsModal/components/EditableText';
import { useAuth } from '../../context/authContext';
import useInternalBooking from '../../hooks/useInternalBooking';
import useLasertagGame from '../../hooks/useLasertagGame';
import useEntertainment from '../../hooks/useEntertainment';
import usePrinterStatus from '../../hooks/usePrinterStatus';
import PrinterCleaningGuideModal from '../PrinterCleaningGuideModal';
import usePlayerNameBlacklist from '../../hooks/usePlayerNameBlacklist';
import {
  PLAYER_NAME_FORBIDDEN_TOAST_DE,
  isPlayerNameForbiddenWordApiError,
} from '../../utils/playerNameApiErrors';

const PLAYER_NAME_MAX_LENGTH = 15;
const PLAYER_NAME_ALLOWED_CHARS_REGEX = /^[\x20-\x7E\u00A0-\u024F\u0300-\u036F\u1E00-\u1EFF]+$/u;

const getBookingRevisionInfo = booking => {
  const rawVersion = Number(booking?.__v);
  const version = Number.isFinite(rawVersion) ? rawVersion : null;
  const editedAtMs = booking?.meta?.editedAt ? new Date(booking.meta.editedAt).getTime() : null;
  const editedAt = Number.isFinite(editedAtMs) ? editedAtMs : null;
  const participantCount = Number(booking?.participantCount || 0);
  const memberCount = Array.isArray(booking?.ltSpecific?.participatingMembers)
    ? booking.ltSpecific.participatingMembers.length
    : 0;

  return { version, editedAt, participantCount, memberCount };
};

const isIncomingBookingStale = (incomingBooking, currentBooking) => {
  if (!incomingBooking || !currentBooking) return false;

  const incomingInfo = getBookingRevisionInfo(incomingBooking);
  const currentInfo = getBookingRevisionInfo(currentBooking);

  if (incomingInfo.version !== null && currentInfo.version !== null) {
    if (incomingInfo.version < currentInfo.version) return true;
    if (incomingInfo.version > currentInfo.version) return false;
  }

  if (incomingInfo.editedAt !== null && currentInfo.editedAt !== null) {
    if (incomingInfo.editedAt < currentInfo.editedAt) return true;
    if (incomingInfo.editedAt > currentInfo.editedAt) return false;
  }

  // Fallback for optimistic local state while requests are still in flight.
  return (
    incomingInfo.participantCount === currentInfo.participantCount &&
    incomingInfo.memberCount < currentInfo.memberCount
  );
};

const hasHydratedParticipatingMembers = booking => {
  const members = booking?.ltSpecific?.participatingMembers || [];
  if (!Array.isArray(members) || members.length === 0) return true;

  return members.every(member => {
    if (!member || typeof member !== 'object') return false;
    if (typeof member._id !== 'string') return false;

    return (
      member.firstName !== undefined ||
      member.playerName !== undefined ||
      member.cardStatus !== undefined
    );
  });
};

function MemberCardQueueModal({
  open,
  onClose,
  booking,
  onBookingUpdated,
  borderColor = null,
  readOnly = false,
}) {
  const {
    addParticipantMember,
    getInternalBookingById,
    removeParticipantMember,
    addNoShowPlaceholder,
    setBookingBirthdayChild,
  } = useInternalBooking();
  const { updateMember } = useLasertagGame();
  const { getEntertainmentById } = useEntertainment();
  const { createEditSuggestion } = usePlayerNameBlacklist();
  const { checkRightAny } = useAuth();
  const canStartCleaning = checkRightAny?.('printerStatus:special:startCleaning');
  const canFlagPlayerNames = checkRightAny?.('internalBooking:update:minor');
  const canManageCards = checkRightAny?.('internalBooking:update:card');
  const { getPrinterStatusById, startCleaning, cancelCleaning } = usePrinterStatus();

  const [localBooking, setLocalBooking] = useState(booking || null);
  const [loading, setLoading] = useState(false);
  const [refreshingBooking, setRefreshingBooking] = useState(false);
  const [readerBooking, setReaderBooking] = useState(null);
  const memberNameRefs = useRef({});
  const placeholderNameRefs = useRef({});
  const memberFirstNameRefs = useRef({});
  const placeholderFirstNameRefs = useRef({});
  const placeholderCounterRef = useRef(0);
  const placeholderKeysBookingIdRef = useRef(null);
  const rowActionLocksRef = useRef(new Set());
  const pendingFocusPlayerNumberRef = useRef(null);
  const focusRetryTimeoutRef = useRef(null);
  const focusRequestIdRef = useRef(0);
  const localBookingRef = useRef(booking || null);
  const modalRef = useRef(null);
  const [memberErrors, setMemberErrors] = useState({});
  const [placeholderErrors, setPlaceholderErrors] = useState({});
  const [placeholderRowKeys, setPlaceholderRowKeys] = useState([]);
  const [rowActionBusy, setRowActionBusy] = useState({});
  const [reprintTarget, setReprintTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);
  const [optionsForMember, setOptionsForMember] = useState(null);
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
  const [showCardNumbersFor, setShowCardNumbersFor] = useState(null);
  const [printerWarnings, setPrinterWarnings] = useState([]);
  const [printerLoaded, setPrinterLoaded] = useState(false);
  const [printerStatus, setPrinterStatus] = useState(null);
  const [printerStatusId, setPrinterStatusId] = useState(null);
  const [printerStatusLastSuccessAt, setPrinterStatusLastSuccessAt] = useState(null);
  const [entertainmentBranchId, setEntertainmentBranchId] = useState(null);
  const [cleaningDialogOpen, setCleaningDialogOpen] = useState(false);
  const [cancelCleaningDialogOpen, setCancelCleaningDialogOpen] = useState(false);
  const [emergencyPrintMode, setEmergencyPrintMode] = useState(false);

  const isReadOnly = !!readOnly || !!localBooking?.finishedAt;

  const getEntertainmentIdForCard = () =>
    localBooking?.entertainmentId?._id || localBooking?.entertainmentId || null;

  const isPlaceholderMember = m => {
    try {
      if (!m) return true;
      if (typeof m === 'string') return false;
      // Check for noshow status or missing name fields
      if (m.cardStatus === 'noshow') return true;
      return !(m.firstName || m.playerName);
    } catch (_) {
      return false;
    }
  };

  const memberHasAnyCard = m => {
    const ids = Array.isArray(m?.cardIDs) ? m.cardIDs : m?.cardID ? [m.cardID] : [];
    return ids.length > 0;
  };

  // Name to address a member by in confirmation copy. A member can carry a first
  // name without a player name (the booker filled in the slot, the guest has not
  // entered their player name yet), so both parts are optional.
  const memberDisplayName = m => {
    const parts = [m?.firstName, m?.playerName].map(s => (s || '').trim()).filter(Boolean);
    return parts.join(' ') || 'diesen Spieler';
  };

  const setMemberFieldError = (id, field, isError) => {
    setMemberErrors(prev => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: !!isError },
    }));
  };

  const setPlaceholderFieldError = (key, field, isError) => {
    setPlaceholderErrors(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: !!isError },
    }));
  };

  const isRowBusy = key => !!rowActionBusy[key];

  const startRowAction = key => {
    if (rowActionLocksRef.current.has(key)) return false;
    rowActionLocksRef.current.add(key);
    setRowActionBusy(prev => ({ ...prev, [key]: true }));
    return true;
  };

  const finishRowAction = key => {
    rowActionLocksRef.current.delete(key);
    setRowActionBusy(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const applyLocalBookingUpdate = useCallback(
    updater => {
      const previousBooking = localBookingRef.current;
      const nextBooking =
        typeof updater === 'function' ? updater(previousBooking) : updater;

      localBookingRef.current = nextBooking;
      setLocalBooking(nextBooking);
      if (onBookingUpdated && nextBooking) onBookingUpdated(nextBooking);
      return nextBooking;
    },
    [onBookingUpdated]
  );

  const reconcilePlaceholderRows = useCallback(
    ({ bookingSnapshot, removedKey = null }) => {
      const bookingId = bookingSnapshot?._id ? String(bookingSnapshot._id) : null;
      const memberCount = Array.isArray(bookingSnapshot?.ltSpecific?.participatingMembers)
        ? bookingSnapshot.ltSpecific.participatingMembers.length
        : 0;
      const targetMissing = Math.max(Number(bookingSnapshot?.participantCount || 0) - memberCount, 0);

      setPlaceholderRowKeys(prev => {
        let base = bookingId && placeholderKeysBookingIdRef.current === bookingId ? prev : [];
        if (removedKey) base = base.filter(key => key !== removedKey);

        if (targetMissing === base.length) return base;
        if (targetMissing < base.length) return base.slice(0, targetMissing);

        const additions = Array.from({ length: targetMissing - base.length }).map(
          () => `${bookingId}:placeholder:${placeholderCounterRef.current++}`
        );
        return [...base, ...additions];
      });
    },
    []
  );

  const toastIfPlayerNameForbiddenApiError = (e, memberId) => {
    if (!isPlayerNameForbiddenWordApiError(e)) return false;
    if (memberId) {
      setMemberFieldError(memberId, 'playerName', true);
      memberNameRefs.current[memberId]?.focus();
    }
    toast.error(PLAYER_NAME_FORBIDDEN_TOAST_DE);
    return true;
  };

  const getInternalBookingByIdRef = useRef(getInternalBookingById);

  useEffect(() => {
    getInternalBookingByIdRef.current = getInternalBookingById;
  }, [getInternalBookingById]);

  useEffect(() => {
    let active = true;

    const incomingBooking = booking || null;
    const currentBooking = localBookingRef.current;
    const incomingBookingId = incomingBooking?._id ? String(incomingBooking._id) : null;
    const currentBookingId = currentBooking?._id ? String(currentBooking._id) : null;

    if (incomingBookingId !== currentBookingId) {
      localBookingRef.current = incomingBooking;
      setLocalBooking(incomingBooking);
      return () => {
        active = false;
      };
    }

    // Ignore only genuinely older parent props so valid reductions still refresh the modal.
    if (isIncomingBookingStale(incomingBooking, currentBooking)) {
      return () => {
        active = false;
      };
    }

    const currentHasHydratedMembers = hasHydratedParticipatingMembers(currentBooking);
    const incomingHasHydratedMembers = hasHydratedParticipatingMembers(incomingBooking);
    if (
      incomingBooking &&
      currentHasHydratedMembers &&
      !incomingHasHydratedMembers &&
      currentBookingId === incomingBookingId
    ) {
      setRefreshingBooking(true);

      const refreshBooking = async () => {
        try {
          const refreshed = await getInternalBookingByIdRef.current(incomingBooking._id);
          if (!active || !refreshed) return;
          localBookingRef.current = refreshed;
          setLocalBooking(refreshed);
        } catch (_) {
          if (!active) return;
          localBookingRef.current = incomingBooking;
          setLocalBooking(incomingBooking);
        } finally {
          if (active) setRefreshingBooking(false);
        }
      };

      refreshBooking();

      return () => {
        active = false;
      };
    }

    localBookingRef.current = incomingBooking;
    setLocalBooking(incomingBooking);

    return () => {
      active = false;
    };
  }, [booking]);

  const entertainmentId =
    typeof localBooking?.entertainmentId === 'string'
      ? localBooking.entertainmentId
      : localBooking?.entertainmentId?._id || null;

  const getEntertainmentByIdRef = useRef(getEntertainmentById);
  const getPrinterStatusByIdRef = useRef(getPrinterStatusById);

  useEffect(() => {
    getEntertainmentByIdRef.current = getEntertainmentById;
  }, [getEntertainmentById]);

  useEffect(() => {
    getPrinterStatusByIdRef.current = getPrinterStatusById;
  }, [getPrinterStatusById]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    const isActive = () => active;

    // Reset when modal opens or entertainment changes
    setPrinterStatusId(null);
    setPrinterStatus(null);
    setPrinterWarnings([]);
    setPrinterLoaded(false);
    setPrinterStatusLastSuccessAt(null);
    setEntertainmentBranchId(null);

    const fetchPrinterStatusId = async () => {
      try {
        if (!entertainmentId) {
          if (!isActive()) return;
          setPrinterLoaded(true);
          return;
        }

        const entertainment = await getEntertainmentByIdRef.current(entertainmentId);
        if (!isActive()) return;

        const psId = entertainment?.printerStatus ? String(entertainment.printerStatus) : null;
        setPrinterStatusId(psId);
        setEmergencyPrintMode(Boolean(entertainment?.emergencyPrintMode));
        const branchRaw =
          entertainment?.branch?._id ||
          entertainment?.branch ||
          entertainment?.branchId?._id ||
          entertainment?.branchId ||
          null;
        setEntertainmentBranchId(branchRaw ? String(branchRaw) : null);
        if (!psId) {
          setPrinterLoaded(true);
        }
      } catch (e) {
        if (!isActive()) return;
        setPrinterLoaded(true);
      }
    };

    fetchPrinterStatusId();

    return () => {
      active = false;
    };
  }, [open, entertainmentId]);

  useEffect(() => {
    if (!open) return undefined;
    if (!printerStatusId) return undefined;

    let active = true;
    const isActive = () => active;

    const computeWarnings = status => {
      const warnings = [];
      const lastUpdatedMs = status?.lastUpdated ? new Date(status.lastUpdated).getTime() : NaN;
      const isOffline = !Number.isFinite(lastUpdatedMs) || Date.now() - lastUpdatedMs > 5 * 60 * 1000;
      if (isOffline || status.connectionOk === false) warnings.push('Drucker nicht erreichbar.');
      if (status.cardsNearEmpty === true) warnings.push('ACHTUNG: Bitte Spielerkarten nachlegen!');
      return warnings;
    };

    const tick = async () => {
      try {
        const status = await getPrinterStatusByIdRef.current(printerStatusId);
        if (!isActive()) return;
        if (!status) return;
        setPrinterStatus(status);
        setPrinterWarnings(computeWarnings(status));
        setPrinterStatusLastSuccessAt(Date.now());
        setPrinterLoaded(true);
      } catch (e) {
        if (!isActive()) return;
        const msg = String(e?.message || '');
        if (msg.includes('404: Printer not found')) {
          setPrinterStatus(null);
          setPrinterWarnings(['Drucker nicht gefunden. Bitte Drucker-Zuordnung prüfen.']);
          setPrinterLoaded(true);
          return;
        }
        setPrinterLoaded(true);
      }
    };

    const id = setInterval(tick, 10000);
    tick();

    return () => {
      active = false;
      clearInterval(id);
    };
  }, [open, printerStatusId]);

  const shouldShowCleaningBanner = useMemo(() => {
    if (!printerLoaded || !printerStatus) return false;
    // Only show banner if cleaning is needed but not yet started
    return (
      printerStatus.cardsBeforeNextCleaning < 100 &&
      printerStatus.shouldStartCleaning !== true
    );
  }, [printerLoaded, printerStatus]);

  const shouldShowCleaningHangingWarning = useMemo(() => {
    if (!printerLoaded || !printerStatus) return false;
    const lastUpdatedMs = printerStatus?.lastUpdated ? new Date(printerStatus.lastUpdated).getTime() : NaN;
    const isOfflineByPrinterTimestamp =
      !Number.isFinite(lastUpdatedMs) || Date.now() - lastUpdatedMs > 5 * 60 * 1000;
    const isOfflineByFrontendPolling =
      !printerStatusLastSuccessAt || Date.now() - printerStatusLastSuccessAt > 5 * 60 * 1000;
    return (
      printerStatus.shouldStartCleaning === true &&
      ((isOfflineByPrinterTimestamp || isOfflineByFrontendPolling) || printerStatus.connectionOk === false)
    );
  }, [printerLoaded, printerStatus, printerStatusLastSuccessAt]);

  const handleOpenCleaningDialog = () => {
    if (!printerStatus?.name) {
      toast.error('Fehler: Druckername fehlt');
      return;
    }
    setCleaningDialogOpen(true);
  };

  const handleCloseCleaningDialog = () => {
    setCleaningDialogOpen(false);
  };

  const handleConfirmStartCleaning = async () => {
    try {
      const printerName = printerStatus?.name;
      if (!printerName) {
        toast.error('Fehler: Druckername fehlt');
        return;
      }
      await startCleaning(printerName);
      setPrinterStatus(prev => (prev ? { ...prev, shouldStartCleaning: true } : prev));
      toast.success(`Reinigung für ${printerName} gestartet`);
    } catch (e) {
      toast.error('Fehler beim Starten der Reinigung');
    }
  };

  const handleOpenCancelCleaningDialog = () => {
    if (!printerStatus?.name) {
      toast.error('Fehler: Druckername fehlt');
      return;
    }
    setCancelCleaningDialogOpen(true);
  };

  const handleCloseCancelCleaningDialog = () => {
    setCancelCleaningDialogOpen(false);
  };

  const handleConfirmCancelCleaning = async () => {
    try {
      const printerName = printerStatus?.name;
      if (!printerName) {
        toast.error('Fehler: Druckername fehlt');
        return;
      }
      await cancelCleaning(printerName);
      setPrinterStatus(prev => (prev ? { ...prev, shouldStartCleaning: false } : prev));
      toast.success(`Reinigung für ${printerName} abgebrochen`);
      handleCloseCancelCleaningDialog();
    } catch (e) {
      toast.error('Fehler beim Abbrechen der Reinigung');
    }
  };

  const membersRaw = useMemo(
    () => localBooking?.ltSpecific?.participatingMembers || [],
    [localBooking]
  );
  const members = useMemo(
    () =>
      (membersRaw || []).map(m =>
        typeof m === 'string'
          ? { _id: String(m), firstName: '', playerName: '', cardStatus: 'open' }
          : {
              ...m,
              firstName: m.firstName || '',
              playerName: m.playerName || '',
              cardStatus: m.cardStatus || 'open',
            }
      ),
    [membersRaw]
  );
  const participantCount = localBooking?.participantCount || 0;
  const missing = Math.max(participantCount - members.length, 0);

  // Resolve which member is the birthday child (bound to a slot via birthdayChildSlotToken).
  // The selection lives on the booking as a slotToken; map it to the linked memberId so the
  // crown can be shown on the correct card row.
  const birthdayChildMemberId = useMemo(() => {
    const token = localBooking?.birthdayChildSlotToken;
    if (!token) return null;
    const slot = (localBooking?.memberSlots || []).find(s => String(s.slotToken) === String(token));
    const memberId = slot?.memberId;
    if (!memberId) return null;
    return typeof memberId === 'object' ? String(memberId._id || memberId) : String(memberId);
  }, [localBooking?.birthdayChildSlotToken, localBooking?.memberSlots]);

  // Only child-birthday bookings carry a birthday child — same tag rule the customer-facing
  // self-service uses, so both sides offer the marking on the same bookings.
  const isChildBirthdayBooking = useMemo(() => {
    const tags = Array.isArray(localBooking?.tags) ? localBooking.tags : [];
    return (
      tags.includes('childBirthday') ||
      tags.includes('childBirthdayBasic') ||
      tags.includes('childBirthdayFull')
    );
  }, [localBooking?.tags]);

  const canMarkBirthdayChild = !isReadOnly && !!canManageCards && isChildBirthdayBooking;

  const focusFirstNameInputAtOrAfter = useCallback(fromPlayerNumber => {
    const container = modalRef.current;
    if (!container) return false;

    const inputs = Array.from(container.querySelectorAll('input[data-input-type="firstName"]'));
    const next = inputs.find(el => Number(el.dataset.playerNumber) >= Number(fromPlayerNumber));

    if (!next) return false;

    next.focus();
    return document.activeElement === next;
  }, []);

  const queueNextFirstNameFocus = useCallback(fromPlayerNumber => {
    focusRequestIdRef.current += 1;
    pendingFocusPlayerNumberRef.current = fromPlayerNumber;

    if (focusRetryTimeoutRef.current) {
      clearTimeout(focusRetryTimeoutRef.current);
      focusRetryTimeoutRef.current = null;
    }
  }, []);

  const schedulePendingFirstNameFocus = useCallback(
    (fromPlayerNumber, requestId, attemptsLeft = 6) => {
      if (focusRetryTimeoutRef.current) {
        clearTimeout(focusRetryTimeoutRef.current);
        focusRetryTimeoutRef.current = null;
      }

      requestAnimationFrame(() => {
        if (focusRequestIdRef.current !== requestId) return;

        if (focusFirstNameInputAtOrAfter(fromPlayerNumber)) {
          pendingFocusPlayerNumberRef.current = null;
          return;
        }

        if (attemptsLeft <= 1) {
          pendingFocusPlayerNumberRef.current = null;
          return;
        }

        focusRetryTimeoutRef.current = setTimeout(() => {
          if (focusRequestIdRef.current !== requestId) return;
          schedulePendingFirstNameFocus(fromPlayerNumber, requestId, attemptsLeft - 1);
        }, 50);
      });
    },
    [focusFirstNameInputAtOrAfter]
  );

  // After localBooking updates (e.g. server refresh), resolve any pending focus request.
  useEffect(() => {
    if (pendingFocusPlayerNumberRef.current === null) return;
    const fromPlayerNumber = pendingFocusPlayerNumberRef.current;
    const requestId = focusRequestIdRef.current;
    schedulePendingFirstNameFocus(fromPlayerNumber, requestId);
  }, [localBooking, placeholderRowKeys, schedulePendingFirstNameFocus]);

  useEffect(
    () => () => {
      focusRequestIdRef.current += 1;
      if (focusRetryTimeoutRef.current) {
        clearTimeout(focusRetryTimeoutRef.current);
      }
    },
    []
  );

  // Auto-focus the first empty firstName field when the modal opens.
  useEffect(() => {
    if (!open) return undefined;
    const focusFirstEmpty = () => {
      const container = modalRef.current;
      if (!container) return false;
      const inputs = Array.from(container.querySelectorAll('input[data-input-type="firstName"]'));
      const firstEmpty = inputs.find(el => !(el.value || '').trim());
      if (firstEmpty) {
        firstEmpty.focus();
        return true;
      }
      return false;
    };
    // Try immediately after paint, then again after modal open animation settles
    requestAnimationFrame(() => {
      if (!focusFirstEmpty()) {
        setTimeout(focusFirstEmpty, 150);
      }
    });
    return undefined;
  }, [open]);

  useEffect(() => {
    const bookingId = localBooking?._id ? String(localBooking._id) : null;
    const bookingChanged = placeholderKeysBookingIdRef.current !== bookingId;

    if (isReadOnly) {
      setPlaceholderRowKeys([]);
      placeholderKeysBookingIdRef.current = bookingId;
      return;
    }

    if (bookingChanged) {
      placeholderNameRefs.current = {};
      placeholderFirstNameRefs.current = {};
      setPlaceholderErrors({});
    }

    if (!bookingId) {
      setPlaceholderRowKeys([]);
      placeholderKeysBookingIdRef.current = bookingId;
      return;
    }

    setPlaceholderRowKeys(prev => {
      const base = bookingChanged ? [] : prev;
      if (missing === base.length) return base;
      if (missing < base.length) return base.slice(0, missing);
      const additions = Array.from({ length: missing - base.length }).map(
        () => `${bookingId}:placeholder:${placeholderCounterRef.current++}`
      );
      return [...base, ...additions];
    });

    placeholderKeysBookingIdRef.current = bookingId;
  }, [localBooking?._id, missing, isReadOnly]);

  const handleConfirmPrint = async member => {
    try {
      const updated = await updateMember({
        memberId: member._id,
        cardStatus: 'confirmed',
        entertainmentId: getEntertainmentIdForCard(),
      });
      const nextStatus = updated?.cardStatus || 'confirmed';
      const merged = updated && typeof updated === 'object' ? { ...member, ...updated } : { ...member, cardStatus: nextStatus };
      setLocalBooking(prev => ({
        ...prev,
        ltSpecific: {
          ...prev.ltSpecific,
          participatingMembers: (prev.ltSpecific?.participatingMembers || []).map(m =>
            m._id === member._id ? { ...m, ...merged, cardStatus: nextStatus } : m
          ),
        },
      }));
      if (onBookingUpdated)
        onBookingUpdated({
          ...localBooking,
          ltSpecific: {
            ...localBooking.ltSpecific,
            participatingMembers: (localBooking.ltSpecific?.participatingMembers || []).map(m =>
              m._id === member._id ? { ...m, ...merged, cardStatus: nextStatus } : m
            ),
          },
        });
    } catch (e) {
      toast.error('Fehler beim Bestätigen');
    }
  };

  const focusNextFirstName = fromPlayerNumber => {
    queueNextFirstNameFocus(fromPlayerNumber);
  };

  const handleRemoveMember = async member => {
    if (isReadOnly) {
      toast.error('Buchung ist ausgecheckt (Read-Only)');
      return;
    }
    const memberId = member && member._id ? String(member._id) : null;
    if (!memberId || !localBooking?._id) return;

    const slot =
      Array.isArray(localBooking.memberSlots) && localBooking.memberSlots.length > 0
        ? localBooking.memberSlots.find(s => s.memberId && String(s.memberId) === memberId)
        : null;

    const removeMemberFromBooking = baseBooking => {
      if (!baseBooking) return baseBooking;
      const filteredParticipants = (baseBooking.ltSpecific?.participatingMembers || []).filter(
        m => {
          const id = typeof m === 'string' ? String(m) : m && m._id ? String(m._id) : null;
          return id !== memberId;
        }
      );
      const nextSlots =
        slot && Array.isArray(baseBooking.memberSlots)
          ? baseBooking.memberSlots.map(s =>
              String(s.slotToken) === String(slot.slotToken) ? { ...s, memberId: undefined } : s
            )
          : baseBooking.memberSlots;

      return {
        ...baseBooking,
        memberSlots: nextSlots,
        ltSpecific: {
          ...(baseBooking.ltSpecific || {}),
          participatingMembers: filteredParticipants,
        },
      };
    };

    const applyManualUpdate = () => {
      setLocalBooking(prev => {
        const updated = removeMemberFromBooking(prev);
        if (onBookingUpdated && updated) onBookingUpdated(updated);
        return updated;
      });
    };

    const shouldBlockUi = !!slot;

    try {
      if (slot) {
        setLoading(true);
        await removeParticipantMember(localBooking._id, { slotToken: slot.slotToken });
        try {
          const updated = await getInternalBookingById(localBooking._id);
          if (updated) {
            setLocalBooking(updated);
            if (onBookingUpdated) onBookingUpdated(updated);
          } else {
            applyManualUpdate();
          }
        } catch (fetchError) {
          console.error('Failed to refresh booking after clearing slot', fetchError);
          applyManualUpdate();
        }
        return;
      }

      // Member without a slot (out-of-sync booking): the same route removes it from
      // ltSpecific.participatingMembers.
      await removeParticipantMember(localBooking._id, { memberId });

      try {
        const updated = await getInternalBookingById(localBooking._id);
        if (updated) {
          setLocalBooking(updated);
          if (onBookingUpdated) onBookingUpdated(updated);
        } else {
          applyManualUpdate();
        }
      } catch (fetchError) {
        console.error('Failed to refresh booking after removing member', fetchError);
        applyManualUpdate();
      }
    } catch (e) {
      toast.error('Fehler beim Entfernen');
    } finally {
      if (shouldBlockUi) setLoading(false);
    }
  };

  const handleAddNoShowPlaceholder = async () => {
    if (isReadOnly) {
      toast.error('Buchung ist ausgecheckt (Read-Only)');
      return;
    }
    try {
      const res = await addNoShowPlaceholder(localBooking._id);
      const newMember = res?.member;

      if (!newMember) {
        toast.error('Fehler beim Markieren als nicht angetreten');
        return;
      }

      setLocalBooking(prev => ({
        ...prev,
        ltSpecific: {
          ...prev.ltSpecific,
          participatingMembers: [
            ...(prev.ltSpecific?.participatingMembers || []),
            { _id: newMember._id, cardStatus: 'noshow' },
          ],
        },
      }));

      if (onBookingUpdated)
        onBookingUpdated({
          ...localBooking,
          ltSpecific: {
            ...localBooking.ltSpecific,
            participatingMembers: [
              ...(localBooking.ltSpecific?.participatingMembers || []),
              { _id: newMember._id, cardStatus: 'noshow' },
            ],
          },
        });
    } catch (e) {
      toast.error('Fehler beim Markieren als nicht angetreten');
    }
  };

  // Marks the member as the booking's birthday child, or removes the marking when they
  // already hold it. Only one member per booking can be marked, so marking someone else
  // simply moves the crown. The backend resolves the member to its slot token.
  const handleToggleBirthdayChild = async member => {
    if (isReadOnly) {
      toast.error('Buchung ist ausgecheckt (Read-Only)');
      return;
    }
    const memberId = member && member._id ? String(member._id) : null;
    if (!memberId || !localBooking?._id) return;

    const rowKey = `member:${memberId}`;
    const isCurrent = !!birthdayChildMemberId && memberId === birthdayChildMemberId;
    if (!startRowAction(rowKey)) return;

    try {
      const res = await setBookingBirthdayChild({
        bookingId: localBooking._id,
        memberId: isCurrent ? null : memberId,
      });
      const nextToken = res?.data?.birthdayChildSlotToken ?? null;
      applyLocalBookingUpdate(prev => ({ ...prev, birthdayChildSlotToken: nextToken }));
    } catch (e) {
      toast.error(
        isCurrent
          ? 'Fehler beim Entfernen der Geburtstagskind-Markierung'
          : 'Fehler beim Markieren des Geburtstagskinds'
      );
    } finally {
      finishRowAction(rowKey);
    }
  };

  const handleSetNameAndConfirm = async (member, name, firstName, nextAfterPlayerNumber) => {
    const rowKey = `member:${member._id}`;
    if (isReadOnly) {
      toast.error('Buchung ist ausgecheckt (Read-Only)');
      return;
    }
    if (!startRowAction(rowKey)) return;
    const trimmed = String(name || '').trim();
    const first = String(firstName || '').trim();
    if (!first) {
      finishRowAction(rowKey);
      setMemberFieldError(member._id, 'firstName', true);
      memberFirstNameRefs.current[member._id]?.focus();
      return;
    }
    if (!trimmed) {
      finishRowAction(rowKey);
      setMemberFieldError(member._id, 'playerName', true);
      memberNameRefs.current[member._id]?.focus();
      return;
    }
    if (trimmed.length > PLAYER_NAME_MAX_LENGTH) {
      finishRowAction(rowKey);
      setMemberFieldError(member._id, 'playerName', true);
      toast.error(`Spielername darf maximal ${PLAYER_NAME_MAX_LENGTH} Zeichen lang sein`);
      memberNameRefs.current[member._id]?.focus();
      return;
    }
    if (!PLAYER_NAME_ALLOWED_CHARS_REGEX.test(trimmed)) {
      finishRowAction(rowKey);
      setMemberFieldError(member._id, 'playerName', true);
      toast.error('Spielername darf keine Emojis enthalten');
      memberNameRefs.current[member._id]?.focus();
      return;
    }
    try {
      const updated = await updateMember({
        memberId: member._id,
        firstName: first,
        playerName: trimmed,
        cardStatus: 'confirmed',
        entertainmentId: getEntertainmentIdForCard(),
      });
      const nextStatus = updated?.cardStatus || 'confirmed';
      const merged = updated && typeof updated === 'object'
        ? { ...member, ...updated, firstName: first, playerName: trimmed }
        : { ...member, firstName: first, playerName: trimmed, cardStatus: nextStatus };
      setMemberErrors(prev => {
        const next = { ...prev };
        delete next[member._id];
        return next;
      });
      setLocalBooking(prev => ({
        ...prev,
        ltSpecific: {
          ...prev.ltSpecific,
          participatingMembers: (prev.ltSpecific?.participatingMembers || []).map(m =>
            m._id === member._id
              ? { ...m, ...merged, firstName: first, playerName: trimmed, cardStatus: nextStatus }
              : m
          ),
        },
      }));
      if (onBookingUpdated)
        onBookingUpdated({
          ...localBooking,
          ltSpecific: {
            ...localBooking.ltSpecific,
            participatingMembers: (localBooking.ltSpecific?.participatingMembers || []).map(m =>
              m._id === member._id
                ? { ...m, ...merged, firstName: first, playerName: trimmed, cardStatus: nextStatus }
                : m
            ),
          },
        });
      if (typeof nextAfterPlayerNumber === 'number') {
        focusNextFirstName(nextAfterPlayerNumber);
      }
    } catch (e) {
      if (toastIfPlayerNameForbiddenApiError(e, member._id)) return;
      toast.error('Fehler beim Aktualisieren');
    } finally {
      finishRowAction(rowKey);
    }
  };

  const handleSetNameOnly = async (member, name, firstName, nextAfterPlayerNumber) => {
    const rowKey = `member:${member._id}`;
    if (isReadOnly) {
      toast.error('Buchung ist ausgecheckt (Read-Only)');
      return;
    }
    if (!startRowAction(rowKey)) return;
    const trimmed = String(name || '').trim();
    const first = String(firstName || '').trim();
    if (!first) {
      finishRowAction(rowKey);
      setMemberFieldError(member._id, 'firstName', true);
      memberFirstNameRefs.current[member._id]?.focus();
      return;
    }
    if (!trimmed) {
      finishRowAction(rowKey);
      setMemberFieldError(member._id, 'playerName', true);
      memberNameRefs.current[member._id]?.focus();
      return;
    }
    if (trimmed.length > PLAYER_NAME_MAX_LENGTH) {
      finishRowAction(rowKey);
      setMemberFieldError(member._id, 'playerName', true);
      toast.error(`Spielername darf maximal ${PLAYER_NAME_MAX_LENGTH} Zeichen lang sein`);
      memberNameRefs.current[member._id]?.focus();
      return;
    }
    if (!PLAYER_NAME_ALLOWED_CHARS_REGEX.test(trimmed)) {
      finishRowAction(rowKey);
      setMemberFieldError(member._id, 'playerName', true);
      toast.error('Spielername darf keine Emojis enthalten');
      memberNameRefs.current[member._id]?.focus();
      return;
    }
    try {
      await updateMember({
        memberId: member._id,
        firstName: first,
        playerName: trimmed,
        entertainmentId: getEntertainmentIdForCard(),
      });
      setMemberErrors(prev => {
        const next = { ...prev };
        delete next[member._id];
        return next;
      });
      setLocalBooking(prev => ({
        ...prev,
        ltSpecific: {
          ...prev.ltSpecific,
          participatingMembers: (prev.ltSpecific?.participatingMembers || []).map(m =>
            m._id === member._id ? { ...m, firstName: first, playerName: trimmed } : m
          ),
        },
      }));
      if (onBookingUpdated)
        onBookingUpdated({
          ...localBooking,
          ltSpecific: {
            ...localBooking.ltSpecific,
            participatingMembers: (localBooking.ltSpecific?.participatingMembers || []).map(m =>
              m._id === member._id ? { ...m, firstName: first, playerName: trimmed } : m
            ),
          },
        });
      if (typeof nextAfterPlayerNumber === 'number') {
        focusNextFirstName(nextAfterPlayerNumber);
      }
    } catch (e) {
      if (toastIfPlayerNameForbiddenApiError(e, member._id)) return;
      toast.error('Fehler beim Aktualisieren');
    } finally {
      finishRowAction(rowKey);
    }
  };

  const handleConfirmAllPrint = async () => {
    try {
      const targets = members.filter(
        m =>
          (m.cardStatus || 'open') === 'open' && !!(m.playerName && m.playerName.trim().length > 0)
      );
      if (targets.length === 0) return;
      const results = await Promise.all(
        targets.map(m =>
          updateMember({
            memberId: m._id,
            cardStatus: 'confirmed',
            entertainmentId: getEntertainmentIdForCard(),
          })
        )
      );
      const byId = new Map(
        results
          .filter(r => r && typeof r === 'object' && r._id)
          .map(r => [String(r._id), r])
      );
      setLocalBooking(prev => ({
        ...prev,
        ltSpecific: {
          ...prev.ltSpecific,
          participatingMembers: (prev.ltSpecific?.participatingMembers || []).map(m =>
            targets.find(t => t._id === m._id)
              ? (() => {
                  const updated = byId.get(String(m._id));
                  const nextStatus = updated?.cardStatus || 'confirmed';
                  return { ...m, ...(updated || {}), cardStatus: nextStatus };
                })()
              : m
          ),
        },
      }));
      if (onBookingUpdated)
        onBookingUpdated({
          ...localBooking,
          ltSpecific: {
            ...localBooking.ltSpecific,
            participatingMembers: (localBooking.ltSpecific?.participatingMembers || []).map(m =>
              targets.find(t => t._id === m._id)
                ? (() => {
                    const updated = byId.get(String(m._id));
                    const nextStatus = updated?.cardStatus || 'confirmed';
                    return { ...m, ...(updated || {}), cardStatus: nextStatus };
                  })()
                : m
            ),
          },
        });
    } catch (e) {
      toast.error('Fehler beim Bestätigen');
    }
  };

  const handleCreateMemberFromPlaceholder = async (refKey, playerNumber, cardStatus) => {
    const rowKey = `placeholder:${refKey}`;
    if (isReadOnly) {
      toast.error('Buchung ist ausgecheckt (Read-Only)');
      return;
    }
    if (!startRowAction(rowKey)) return;
    const name = (placeholderNameRefs.current[refKey]?.value || '').trim();
    const first = (placeholderFirstNameRefs.current[refKey]?.value || '').trim();

    if (!first) {
      finishRowAction(rowKey);
      setPlaceholderFieldError(refKey, 'firstName', true);
      placeholderFirstNameRefs.current[refKey]?.focus();
      return;
    }
    if (!name) {
      finishRowAction(rowKey);
      setPlaceholderFieldError(refKey, 'playerName', true);
      placeholderNameRefs.current[refKey]?.focus();
      return;
    }
    if (name.length > PLAYER_NAME_MAX_LENGTH) {
      finishRowAction(rowKey);
      setPlaceholderFieldError(refKey, 'playerName', true);
      toast.error(`Spielername darf maximal ${PLAYER_NAME_MAX_LENGTH} Zeichen lang sein`);
      placeholderNameRefs.current[refKey]?.focus();
      return;
    }
    if (!PLAYER_NAME_ALLOWED_CHARS_REGEX.test(name)) {
      finishRowAction(rowKey);
      setPlaceholderFieldError(refKey, 'playerName', true);
      toast.error('Spielername darf keine Emojis enthalten');
      placeholderNameRefs.current[refKey]?.focus();
      return;
    }

    try {
      const res = await addParticipantMember({
        bookingId: localBooking._id,
        firstName: first,
        playerName: name,
        cardStatus,
      });

      const fallbackMember = {
        _id: res?.member?._id,
        firstName: first,
        playerName: name,
        cardStatus,
      };
      const nextMember =
        res?.member && typeof res.member === 'object' ? { ...fallbackMember, ...res.member } : fallbackMember;

      // Optimistically add the new member to local state.
      // The backend is now atomic, so this is safe and avoids stale-read races
      // that occur when multiple concurrent requests each do a server refresh.
      const memberId = nextMember._id ? String(nextMember._id) : null;
      const nextBooking = applyLocalBookingUpdate(prev => {
        const existing = prev.ltSpecific?.participatingMembers || [];
        const alreadyPresent = memberId && existing.some(m => String(m._id || m) === memberId);
        if (alreadyPresent) return prev;
        return {
          ...prev,
          ltSpecific: { ...prev.ltSpecific, participatingMembers: [...existing, nextMember] },
        };
      });

      setPlaceholderErrors(prev => {
        const next = { ...prev };
        delete next[refKey];
        return next;
      });
      delete placeholderNameRefs.current[refKey];
      delete placeholderFirstNameRefs.current[refKey];
      reconcilePlaceholderRows({ bookingSnapshot: nextBooking, removedKey: refKey });

      // When the new member stays in the open list, the next editable row shifts down by one.
      // When the member is confirmed immediately, the next editable row moves into the same number.
      const nextFocusPlayerNumber = cardStatus === 'confirmed' ? playerNumber : playerNumber + 1;
      queueNextFirstNameFocus(nextFocusPlayerNumber);
    } catch (e) {
      if (isPlayerNameForbiddenWordApiError(e)) {
        setPlaceholderFieldError(refKey, 'playerName', true);
        toast.error(PLAYER_NAME_FORBIDDEN_TOAST_DE);
        placeholderNameRefs.current[refKey]?.focus();
        return;
      }
      if (e?.code === 'NO_FREE_SLOTS' || e?.code === 'BOOKING_CONFLICT') {
        try {
          const updated = await getInternalBookingById(localBooking._id);
          if (updated) {
            applyLocalBookingUpdate(updated);
          }
        } catch (_) {
          // ignore refresh error
        }
        toast.error('Keine freien Plätze mehr in dieser Buchung');
      } else {
        toast.error('Fehler beim Anlegen');
      }
    } finally {
      finishRowAction(rowKey);
    }
  };

  const renderMemberRow = (member, playerNumber, isLast = false) => {
    const status = member.cardStatus || 'open';
    const rowBusy = isRowBusy(`member:${member._id}`);
    const hasName = !!(member.playerName && member.playerName.trim().length > 0);
    const hasAnyCard = memberHasAnyCard(member);
    const isReprintFlow = status === 'reprint_requested' || status === 'printing';
    const isCompleted = status === 'printed' || (hasAnyCard && !isReprintFlow);
    const isPlaceholder = isPlaceholderMember(member);
    // A row only asks for a name while its card has not been started yet. Once the card is
    // confirmed, printing or done, the name area switches to the editable display, so a missing
    // player name can still be filled in without re-triggering a print.
    const awaitingName = !isPlaceholder && !hasName && status === 'open' && !isCompleted;
    const isBirthdayChild =
      !!birthdayChildMemberId && String(member._id) === birthdayChildMemberId;

    return (
      <Box
        key={member._id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.75,
          borderBottom: isLast ? 'none' : '1px solid',
          borderColor: 'divider',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ width: 40, fontWeight: 'bold' }}>{playerNumber}.</Box>
        {isBirthdayChild && (
          <Chip
            label={
              <span role='img' aria-label='Geburtstagskind'>
                👑 Geburtstagskind
              </span>
            }
            size='small'
            sx={{
              mr: 1,
              fontWeight: 'bold',
              bgcolor: '#FFF4CC',
              color: '#8a6d00',
              border: '1px solid #E6C200',
            }}
          />
        )}
        {isPlaceholder ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: 1,
              mr: { xs: 0, sm: 1, md: 5 },
            }}
          >
            <Chip label='Nicht angetreten' color='default' size='small' sx={{ mr: 1 }} />
            {!isReadOnly && !!canManageCards && (
              <Box sx={{ ml: 'auto' }}>
                <Button
                  variant='outlined'
                  size='small'
                  color='inherit'
                  onClick={() => handleRemoveMember(member)}
                  sx={{ height: '32px' }}
                >
                  Slot freigeben
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          (!awaitingName || isReadOnly) && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
                gap: { xs: 1, sm: 2, md: 3 },
                alignItems: 'center',
                flexWrap: 'wrap',
                width: { xs: '100%', sm: 'auto' },
                flex: 1,
                minWidth: 0,
                mr: { xs: 0, sm: 1, md: 5 },
              }}
            >
              <EditableText
                value={member.firstName || ''}
                emptyText='—'
                disabled={isReadOnly}
                onSave={async val => {
                  const trimmed = String(val || '').trim();
                  if (!trimmed) throw new Error('Vorname darf nicht leer sein');
                  await updateMember({ memberId: member._id, firstName: trimmed });
                  setLocalBooking(prev => ({
                    ...prev,
                    ltSpecific: {
                      ...prev.ltSpecific,
                      participatingMembers: (prev.ltSpecific?.participatingMembers || []).map(m =>
                        m._id === member._id ? { ...m, firstName: trimmed } : m
                      ),
                    },
                  }));
                  if (onBookingUpdated)
                    onBookingUpdated({
                      ...localBooking,
                      ltSpecific: {
                        ...localBooking.ltSpecific,
                        participatingMembers: (
                          localBooking.ltSpecific?.participatingMembers || []
                        ).map(m => (m._id === member._id ? { ...m, firstName: trimmed } : m)),
                      },
                    });
                }}
                sx={{ minWidth: 0, p: 0 }}
              />
              <EditableText
                value={member.playerName || ''}
                emptyText='—'
                disabled={isReadOnly}
                onSave={async val => {
                  const trimmed = String(val || '').trim();
                  if (!trimmed) throw new Error('Spielername darf nicht leer sein');
                  if (trimmed.length > PLAYER_NAME_MAX_LENGTH)
                    throw new Error(
                      `Spielername darf maximal ${PLAYER_NAME_MAX_LENGTH} Zeichen lang sein`
                    );
                  if (!PLAYER_NAME_ALLOWED_CHARS_REGEX.test(trimmed))
                    throw new Error('Spielername darf keine Emojis enthalten');
                  await updateMember({
                    memberId: member._id,
                    playerName: trimmed,
                    entertainmentId: getEntertainmentIdForCard(),
                  });
                  setLocalBooking(prev => ({
                    ...prev,
                    ltSpecific: {
                      ...prev.ltSpecific,
                      participatingMembers: (prev.ltSpecific?.participatingMembers || []).map(m =>
                        m._id === member._id ? { ...m, playerName: trimmed } : m
                      ),
                    },
                  }));
                  if (onBookingUpdated)
                    onBookingUpdated({
                      ...localBooking,
                      ltSpecific: {
                        ...localBooking.ltSpecific,
                        participatingMembers: (
                          localBooking.ltSpecific?.participatingMembers || []
                        ).map(m => (m._id === member._id ? { ...m, playerName: trimmed } : m)),
                      },
                    });
                }}
                onSaveAndReport={
                  !isReadOnly && canFlagPlayerNames
                    ? async ({ previousValue, nextValue }) => {
                        const trimmed = String(nextValue || '').trim();
                        if (!trimmed) throw new Error('Spielername darf nicht leer sein');
                        if (trimmed.length > PLAYER_NAME_MAX_LENGTH)
                          throw new Error(
                            `Spielername darf maximal ${PLAYER_NAME_MAX_LENGTH} Zeichen lang sein`
                          );
                        if (!PLAYER_NAME_ALLOWED_CHARS_REGEX.test(trimmed))
                          throw new Error('Spielername darf keine Emojis enthalten');

                        await updateMember({ memberId: member._id, playerName: trimmed });
                        setLocalBooking(prev => ({
                          ...prev,
                          ltSpecific: {
                            ...prev.ltSpecific,
                            participatingMembers: (prev.ltSpecific?.participatingMembers || []).map(m =>
                              m._id === member._id ? { ...m, playerName: trimmed } : m
                            ),
                          },
                        }));
                        if (onBookingUpdated)
                          onBookingUpdated({
                            ...localBooking,
                            ltSpecific: {
                              ...localBooking.ltSpecific,
                              participatingMembers: (
                                localBooking.ltSpecific?.participatingMembers || []
                              ).map(m => (m._id === member._id ? { ...m, playerName: trimmed } : m)),
                            },
                          });

                        const oldName = String(previousValue || '').trim();
                        if (!oldName) return;
                        if (!entertainmentBranchId) {
                          toast.error('Name gespeichert, aber Meldung fehlgeschlagen (Filiale unbekannt)');
                          return;
                        }

                        try {
                          const resp = await createEditSuggestion({
                            oldPlayerName: oldName,
                            branchId: entertainmentBranchId,
                            memberId: member._id,
                            bookingId: localBooking?._id || undefined,
                          });
                          if (resp) {
                            toast.success('Alter Name wurde gemeldet');
                          }
                        } catch (e) {
                          toast.error('Name gespeichert, aber Meldung fehlgeschlagen');
                        }
                      }
                    : undefined
                }
                sx={{ minWidth: 0, p: 0 }}
              />
            </Box>
          )
        )}
        {isCompleted && (
          <Chip
            label='Abgeschlossen'
            color='success'
            size='small'
            sx={{ mr: 1 }}
            icon={<DoneIcon fontSize='small' />}
          />
        )}
        {!isPlaceholder && !isCompleted && status === 'link_failed' && (
          <Chip
            label='Verknüpfen fehlgeschlagen'
            color='warning'
            size='small'
            sx={{ mr: 1, cursor: 'pointer' }}
            onClick={() => setLinkTarget(member)}
            clickable
          />
        )}
        {!isPlaceholder && !isCompleted && status === 'print_failed' && (
          <Chip
            label='Druck fehlgeschlagen'
            color='error'
            size='small'
            sx={{ mr: 1 }}
            icon={<PrintIcon fontSize='small' />}
          />
        )}
        {!isPlaceholder && !isCompleted && status === 'reprint_requested' && (
          <Chip
            label='Weitere Karte wird gedruckt'
            color='info'
            size='small'
            sx={{ mr: 1 }}
            icon={<PrintIcon fontSize='small' />}
          />
        )}
        {!isPlaceholder && !isCompleted && (status === 'confirmed' || status === 'printing') && (
          <Chip
            label={
              status === 'printing' && hasAnyCard ? 'Weitere Karte wird gedruckt' : 'Wird gedruckt'
            }
            color='info'
            size='small'
            sx={{ mr: 1 }}
            icon={<PrintIcon fontSize='small' />}
          />
        )}
        {/* card numbers moved to modal via options menu */}
        {!isPlaceholder &&
          !isCompleted &&
          status !== 'confirmed' &&
          status !== 'printing' &&
          status !== 'reprint_requested' &&
          (hasName ? (
            status === 'open' ? (
              <Button
                variant='contained'
                size='small'
                onClick={() => handleConfirmPrint(member)}
                sx={{ height: '40px' }}
              >
                Druck bestätigen
              </Button>
            ) : null
          ) : (
            awaitingName &&
            !isReadOnly && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexDirection: { xs: 'column', sm: 'row' },
                  width: { xs: '100%', sm: 'auto' },
                  flex: 1,
                  minWidth: 0,
                  alignItems: 'center',
                }}
              >
                <TextField
                  placeholder='Vorname'
                  size='small'
                  inputRef={el => {
                    if (el) memberFirstNameRefs.current[member._id] = el;
                  }}
                  inputProps={{ 'data-player-number': playerNumber, 'data-input-type': 'firstName' }}
                  required
                  error={!!memberErrors[member._id]?.firstName}
                  defaultValue={member.firstName || ''}
                  disabled={rowBusy}
                  onChange={e => {
                    const v = (e.target.value || '').trim();
                    if (memberErrors[member._id]?.firstName)
                      setMemberFieldError(member._id, 'firstName', v.length === 0);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (e.repeat || rowBusy) return;
                      memberNameRefs.current[member._id]?.focus();
                    }
                  }}
                  sx={{ width: { xs: '100%', sm: 180 } }}
                />
                <TextField
                  placeholder='Spielername'
                  size='small'
                  inputRef={el => {
                    if (el) memberNameRefs.current[member._id] = el;
                  }}
                  inputProps={{ maxLength: PLAYER_NAME_MAX_LENGTH }}
                  required
                  error={!!memberErrors[member._id]?.playerName}
                  disabled={rowBusy}
                  onChange={e => {
                    const v = (e.target.value || '').trim();
                    if (memberErrors[member._id]?.playerName)
                      setMemberFieldError(member._id, 'playerName', v.length === 0);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (e.repeat || rowBusy) return;
                      const value = (memberNameRefs.current[member._id]?.value || '').trim();
                      const first = (memberFirstNameRefs.current[member._id]?.value || '').trim();
                      handleSetNameOnly(member, value, first, playerNumber);
                    }
                  }}
                  sx={{ width: { xs: '100%', sm: 220 } }}
                />

                <Box sx={{ width: { xs: '100%', sm: 'auto' }, ml: { xs: 0, sm: 'auto' } }}>
                  <Button
                    variant='outlined'
                    size='small'
                    fullWidth
                    disabled={rowBusy}
                    onClick={() => {
                      const value = (memberNameRefs.current[member._id]?.value || '').trim();
                      const first = (memberFirstNameRefs.current[member._id]?.value || '').trim();
                      handleSetNameOnly(member, value, first, playerNumber);
                    }}
                    sx={{ height: '40px' }}
                  >
                    Anlegen
                  </Button>
                </Box>
                <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    variant='contained'
                    size='small'
                    fullWidth
                    disabled={rowBusy}
                    onClick={() => {
                      const value = (memberNameRefs.current[member._id]?.value || '').trim();
                      const first = (memberFirstNameRefs.current[member._id]?.value || '').trim();
                      handleSetNameAndConfirm(member, value, first, playerNumber);
                    }}
                    sx={{ height: '40px' }}
                  >
                    Name setzen & bestätigen
                  </Button>
                </Box>
              </Box>
            )
          ))}
        {/* Remove (open) — also offered while the player name is still missing, so a
            slot the booker filled in with a first name only can be freed without
            having to invent and create a player name first. */}
        {!isPlaceholder && status === 'open' && !isReadOnly && !!canManageCards && (
          <Button
            variant='text'
            size='small'
            onClick={() => setRemoveTarget(member)}
            sx={{ height: '32px', ml: 1 }}
          >
            Aus Buchung entfernen
          </Button>
        )}
        {/* Further options (any non-open member) — none of the entries below read a player
            name, so a member whose card is already printed stays removable without one. */}
        {!isPlaceholder && status !== 'open' && (
          <Button
            variant='text'
            size='small'
            onClick={e => {
              setOptionsForMember(member);
              setOptionsAnchorEl(e.currentTarget);
            }}
            sx={{ height: '32px', ml: 1 }}
          >
            Weitere Optionen
          </Button>
        )}
        {/* Birthday-child toggle — available in every card status, so the crown can be set
            before the cards are printed as well as afterwards. */}
        {!isPlaceholder && canMarkBirthdayChild && (
          <IconButton
            size='small'
            disabled={rowBusy}
            onClick={() => handleToggleBirthdayChild(member)}
            title={
              isBirthdayChild
                ? 'Geburtstagskind-Markierung entfernen'
                : 'Als Geburtstagskind markieren'
            }
            aria-label={
              isBirthdayChild
                ? 'Geburtstagskind-Markierung entfernen'
                : 'Als Geburtstagskind markieren'
            }
            sx={{
              ml: 1,
              opacity: isBirthdayChild ? 1 : 0.35,
              filter: isBirthdayChild ? 'none' : 'grayscale(1)',
            }}
          >
            <span role='img' aria-hidden='true'>
              👑
            </span>
          </IconButton>
        )}
      </Box>
    );
  };

  const renderPlaceholderRow = (playerNumber, placeholderKey, isLast = false) => {
    const refKey = placeholderKey;
    const rowBusy = isRowBusy(`placeholder:${refKey}`);
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.75,
          borderBottom: isLast ? 'none' : '1px solid',
          borderColor: 'divider',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ width: 40, fontWeight: 'bold' }}>{playerNumber}.</Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', sm: 'auto' },
            flex: 1,
            minWidth: 0,
            alignItems: 'center',
          }}
        >
          <TextField
            placeholder='Vorname'
            size='small'
            inputRef={el => {
              if (el) placeholderFirstNameRefs.current[refKey] = el;
            }}
            inputProps={{ 'data-player-number': playerNumber, 'data-input-type': 'firstName' }}
            required
            error={!!placeholderErrors[refKey]?.firstName}
            disabled={rowBusy}
            onChange={e => {
              const v = (e.target.value || '').trim();
              if (placeholderErrors[refKey]?.firstName)
                setPlaceholderFieldError(refKey, 'firstName', v.length === 0);
            }}
            onKeyDown={async e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (e.repeat || rowBusy) return;
                placeholderNameRefs.current[refKey]?.focus();
              }
            }}
            sx={{ width: { xs: '100%', sm: 180 } }}
          />
          <TextField
            placeholder='Spielername'
            size='small'
            inputRef={el => {
              if (el) placeholderNameRefs.current[refKey] = el;
            }}
            inputProps={{ maxLength: PLAYER_NAME_MAX_LENGTH }}
            required
            error={!!placeholderErrors[refKey]?.playerName}
            disabled={rowBusy}
            onChange={e => {
              const v = (e.target.value || '').trim();
              if (placeholderErrors[refKey]?.playerName)
                setPlaceholderFieldError(refKey, 'playerName', v.length === 0);
            }}
            onKeyDown={async e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (e.repeat || rowBusy) return;
                const name = (placeholderNameRefs.current[refKey]?.value || '').trim();
                const first = (placeholderFirstNameRefs.current[refKey]?.value || '').trim();
                if (!first) {
                  setPlaceholderFieldError(refKey, 'firstName', true);
                  placeholderFirstNameRefs.current[refKey]?.focus();
                  return;
                }
                if (!name) {
                  setPlaceholderFieldError(refKey, 'playerName', true);
                  placeholderNameRefs.current[refKey]?.focus();
                  return;
                }
                if (name.length > PLAYER_NAME_MAX_LENGTH) {
                  setPlaceholderFieldError(refKey, 'playerName', true);
                  toast.error(
                    `Spielername darf maximal ${PLAYER_NAME_MAX_LENGTH} Zeichen lang sein`
                  );
                  placeholderNameRefs.current[refKey]?.focus();
                  return;
                }
                await handleCreateMemberFromPlaceholder(refKey, playerNumber, 'open');
              }
            }}
            sx={{ width: { xs: '100%', sm: 220 } }}
          />

          <Box sx={{ width: { xs: '100%', sm: 'auto' }, ml: { xs: 0, sm: 'auto' } }}>
            <Button
              variant='outlined'
              size='small'
              fullWidth
              disabled={rowBusy}
              onClick={async () => {
                await handleCreateMemberFromPlaceholder(refKey, playerNumber, 'open');
              }}
              sx={{ height: '40px' }}
            >
              Anlegen
            </Button>
          </Box>
          <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant='contained'
              size='small'
              fullWidth
              disabled={rowBusy}
              onClick={async () => {
                await handleCreateMemberFromPlaceholder(refKey, playerNumber, 'confirmed');
              }}
              sx={{ height: '40px' }}
            >
              Anlegen & Drucken
            </Button>
          </Box>
          <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant='text'
              size='small'
              fullWidth
              onClick={handleAddNoShowPlaceholder}
              sx={{ height: '40px' }}
            >
              Nicht angetreten
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  const allItems = useMemo(() => {
    const membersOpen = members.filter(m => {
      const status = m.cardStatus || 'open';
      return (
        !isPlaceholderMember(m) &&
        status !== 'confirmed' &&
        status !== 'printing' &&
        status !== 'printed' &&
        !memberHasAnyCard(m)
      );
    });
    const membersConfirmed = members.filter(m => {
      const status = m.cardStatus || 'open';
      return (
        !isPlaceholderMember(m) &&
        (status === 'confirmed' || status === 'printing') &&
        !memberHasAnyCard(m)
      );
    });
    const membersCompleted = members.filter(m => {
      const status = m.cardStatus || 'open';
      return isPlaceholderMember(m) || status === 'printed' || memberHasAnyCard(m);
    });

    const aboveItems = [
      ...membersOpen.map(m => ({ type: 'member', member: m })),
      ...placeholderRowKeys.map((placeholderKey, idx) => ({
        type: 'placeholder',
        placeholderKey,
        placeholderIndex: idx,
      })),
    ];

    const belowItems = [
      ...membersConfirmed.map(m => ({ type: 'member', member: m })),
      ...membersCompleted.map(m => ({ type: 'member', member: m })),
    ];

    const withDivider =
      aboveItems.length > 0 && belowItems.length > 0
        ? [...aboveItems, { type: 'divider' }, ...belowItems]
        : [...aboveItems, ...belowItems];

    let displayIndex = 0;
    const withDisplayIndex = withDivider.map(item => {
      if (item.type === 'member' || item.type === 'placeholder') {
        displayIndex += 1;
        return { ...item, displayIndex };
      }
      return item;
    });

    return withDisplayIndex;
  }, [members, placeholderRowKeys]);

  const lastNonDividerIndex = useMemo(() => {
    let last = -1;
    (allItems || []).forEach((it, idx) => {
      if (it && it.type !== 'divider') last = idx;
    });
    return last;
  }, [allItems]);

  const canBulkConfirm = useMemo(
    () =>
      members.some(
        m =>
          (m.cardStatus || 'open') === 'open' && !!(m.playerName && m.playerName.trim().length > 0)
      ),
    [members]
  );
  const unassignedSlots = missing;

  // The locationserver only actually triggers print jobs for bookings of the
  // current day, so surface a hint when this booking is for another day.
  const isBookingFromToday = useMemo(() => {
    if (!localBooking?.startTime) return true;
    const start = dayjs(localBooking.startTime);
    return start.isValid() ? start.isSame(dayjs(), 'day') : true;
  }, [localBooking?.startTime]);

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby='member-card-queue-modal'
        aria-describedby='member-card-queue-modal-description'
        sx={{
          '& .MuiBackdrop-root': { outline: 'none' },
          '& .MuiModal-root': { outline: 'none' },
        }}
      >
        <Box
          ref={modalRef}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '1000px',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            overflow: 'auto',
            borderRadius: '10px',
            outline: 'none',
            ...(borderColor && { border: `2px solid ${borderColor}` }),
          }}
        >
          {(loading || refreshingBooking) && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && !refreshingBooking && !localBooking && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                my: 8,
              }}
            >
              <PersonAddDisabledIcon sx={{ fontSize: 80, color: '#9ca3af', mb: 2, opacity: 0.6 }} />
              <Typography variant='h6' sx={{ color: '#6b7280' }}>
                Keine Daten
              </Typography>
            </Box>
          )}

          {!loading && !refreshingBooking && localBooking && (
            <>
              <Box sx={{ position: 'relative', mb: 2 }}>
                <IconButton
                  onClick={onClose}
                  sx={{
                    position: 'absolute',
                    top: '-20px',
                    right: '-20px',
                    zIndex: 1,
                  }}
                >
                  <CloseIcon />
                </IconButton>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1.5,
                    pr: 6,
                  }}
                >
                  <Typography
                    variant='h6'
                    sx={{ mr: { sm: 2 }, textAlign: { xs: 'left', sm: 'inherit' } }}
                  >
                    Spielerkarten verwalten –{' '}
                    {dayjs(localBooking.startTime).format('DD.MM.YYYY HH:mm')} –{' '}
                    {localBooking.firstName} {localBooking.lastName} (#{localBooking.bookingNumber})
                  </Typography>
                  {isReadOnly && (
                    <Chip
                      label='Read-Only (ausgecheckt)'
                      color='default'
                      size='small'
                      variant='outlined'
                      sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    />
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      width: { xs: '100%', sm: 'auto' },
                      flexDirection: { xs: 'column', sm: 'row' },
                    }}
                  >
                    {canBulkConfirm && (
                      <Button
                        size='small'
                        variant='contained'
                        onClick={handleConfirmAllPrint}
                        fullWidth
                      >
                        Alle drucken
                      </Button>
                    )}
                    {!isReadOnly && unassignedSlots > 0 && (
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => setReaderBooking(localBooking)}
                        fullWidth
                      >
                        Vorhandene Karten einlesen
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
              {printerLoaded && (shouldShowCleaningBanner || shouldShowCleaningHangingWarning || printerStatus?.shouldStartCleaning === true || printerWarnings.length > 0) && (
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    fontWeight: '500',
                  }}
                >
                  {shouldShowCleaningHangingWarning && (
                    <Alert
                      severity='error'
                      sx={{ fontWeight: '500' }}
                      action={
                        canStartCleaning ? (
                          <Button
                            size='small'
                            color='inherit'
                            onClick={handleOpenCancelCleaningDialog}
                            sx={{ mr: 1 }}
                          >
                            Reinigung abbrechen
                          </Button>
                        ) : null
                      }
                    >
                      <Typography sx={{ fontWeight: 600, mb: 1 }}>
                        Drucker antwortet nicht - Reinigung hängt möglicherweise
                      </Typography>
                      <Typography component='div' sx={{ fontSize: '0.875rem' }}>
                        Bitte prüfe folgende Schritte:
                        <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
                          <li>Das Farbband muss entfernt werden</li>
                          <li>Die Reinigungskarte muss eingelegt sein</li>
                          <li>Der Schieberegler für Kartenbreite muss auf das Maximum eingerichtet sein</li>
                        </ul>
                        Nachdem alle Schritte befolgt wurden, starte die Reinigung erneut.
                      </Typography>
                    </Alert>
                  )}
                  {printerStatus?.shouldStartCleaning === true && !shouldShowCleaningHangingWarning && (
                    <Alert
                      severity='warning'
                      sx={{ fontWeight: '500' }}
                      action={
                        canStartCleaning ? (
                          <Button
                            size='small'
                            color='inherit'
                            onClick={handleOpenCancelCleaningDialog}
                            sx={{ mr: 1 }}
                          >
                            Reinigung abbrechen
                          </Button>
                        ) : null
                      }
                    >
                      Reinigung läuft...
                    </Alert>
                  )}
                  {shouldShowCleaningBanner && !shouldShowCleaningHangingWarning && !printerStatus?.shouldStartCleaning && (
                    <Alert
                      severity='warning'
                      sx={{ fontWeight: '500' }}
                      action={
                        canStartCleaning ? (
                          <Button
                            variant='contained'
                            color='warning'
                            size='medium'
                            startIcon={<CleaningServicesIcon />}
                            onClick={handleOpenCleaningDialog}
                            disabled={printerStatus?.shouldStartCleaning === true}
                            sx={{ 
                              fontWeight: 600,
                              textTransform: 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Reinigung jetzt durchführen
                          </Button>
                        ) : null
                      }
                    >
                      Reinigung bald erforderlich. Vor der Reinigung muss das Farbband entfernt
                      werden, die Reinigungskarte muss eingelegt sein und der Schieberegler für
                      Kartenbreite muss auf das Maximum eingerichtet sein.
                    </Alert>
                  )}
                  {printerWarnings.map((w, idx) => (
                    <Alert
                      sx={{ fontWeight: '500' }}
                      key={idx}
                      severity={
                        w === 'ACHTUNG: Bitte Spielerkarten nachlegen!' ||
                        w === 'ACHTUNG: Bitte Farbband entfernen!'
                          ? 'warning'
                          : 'error'
                      }
                    >
                      {w}
                    </Alert>
                  ))}
                </Box>
              )}
              {emergencyPrintMode && (
                <Alert severity='warning' sx={{ mb: 2 }}>
                  Notfallmodus aktiv: Karten werden nicht gedruckt, sondern müssen manuell
                  beschriftet und anschließend über „Karte verknüpfen“ zugeordnet werden.
                </Alert>
              )}
              {!isBookingFromToday && (
                <Alert severity='info' sx={{ mb: 2 }}>
                  Diese Buchung ist nicht von heute. Der Locationserver löst Druckvorgänge nur für
                  Buchungen des laufenden Tages tatsächlich aus. Eingaben werden gespeichert, es wird
                  aktuell jedoch keine Karte gedruckt.
                </Alert>
              )}
              <Box>
                {allItems.map((item, i) => {
                  const nextIsDivider = allItems[i + 1]?.type === 'divider';
                  const isLast = nextIsDivider || i === lastNonDividerIndex;
                  const playerNumber = item.type === 'divider' ? null : item.displayIndex;
                  if (item.type === 'member') {
                    return (
                      <div key={item.member._id}>
                        {renderMemberRow(item.member, playerNumber, isLast)}
                      </div>
                    );
                  }
                  if (item.type === 'divider') {
                    return (
                      <Box key={`action-divider`} sx={{ my: 2 }}>
                        <Divider
                          sx={{
                            '&::before, &::after': { borderTopWidth: 2, borderTopStyle: 'dashed' },
                          }}
                        />
                      </Box>
                    );
                  }
                  return (
                    <div key={`placeholder-${item.placeholderKey}`}>
                      {renderPlaceholderRow(playerNumber, item.placeholderKey, isLast)}
                    </div>
                  );
                })}
              </Box>
              <Menu
                anchorEl={optionsAnchorEl}
                open={!!optionsAnchorEl}
                onClose={() => {
                  setOptionsAnchorEl(null);
                  setOptionsForMember(null);
                }}
              >
                <MenuItem
                  onClick={() => {
                    if (optionsForMember) setShowCardNumbersFor(optionsForMember);
                    setOptionsAnchorEl(null);
                    setOptionsForMember(null);
                  }}
                >
                  Karteninformationen anzeigen
                </MenuItem>
                {!!canManageCards && (
                  <MenuItem
                    onClick={() => {
                      if (optionsForMember) setLinkTarget(optionsForMember);
                      setOptionsAnchorEl(null);
                      setOptionsForMember(null);
                    }}
                  >
                    {optionsForMember && memberHasAnyCard(optionsForMember)
                      ? 'Weitere Karte verknüpfen'
                      : 'Karte verknüpfen'}
                  </MenuItem>
                )}
                {!!canManageCards && (
                  <MenuItem
                    onClick={() => {
                      if (optionsForMember) setReprintTarget(optionsForMember);
                      setOptionsAnchorEl(null);
                      setOptionsForMember(null);
                    }}
                  >
                    Weitere Karte drucken
                  </MenuItem>
                )}
                {!isReadOnly && !!canManageCards && (
                  <MenuItem
                    onClick={() => {
                      if (optionsForMember) setRemoveTarget(optionsForMember);
                      setOptionsAnchorEl(null);
                      setOptionsForMember(null);
                    }}
                  >
                    Aus Buchung entfernen
                  </MenuItem>
                )}
              </Menu>
            </>
          )}

          {!isReadOnly && (
            <ReadMemberCards
              isOpen={!!readerBooking}
              onClose={() => setReaderBooking(null)}
              booking={readerBooking || null}
              onMemberAdded={newMember => {
                if (!readerBooking) return;
                // if server said already part of booking, do nothing
                if (newMember === null) return;
                setLocalBooking(prev => ({
                  ...prev,
                  ltSpecific: {
                    ...prev.ltSpecific,
                    participatingMembers: [
                      ...(prev.ltSpecific?.participatingMembers || []),
                      newMember ? newMember : { cardStatus: 'open' },
                    ],
                  },
                }));
                if (onBookingUpdated)
                  onBookingUpdated({
                    ...localBooking,
                    ltSpecific: {
                      ...localBooking.ltSpecific,
                      participatingMembers: [
                        ...(localBooking.ltSpecific?.participatingMembers || []),
                        newMember ? newMember : { cardStatus: 'open' },
                      ],
                    },
                  });
              }}
            />
          )}
          <ReadMemberCards
            isOpen={!!linkTarget}
            onClose={() => setLinkTarget(null)}
            booking={localBooking || null}
            linkMode
            linkMemberId={linkTarget?._id || null}
            onCardLinked={({ memberId, cardID }) => {
              setLocalBooking(prev => ({
                ...prev,
                ltSpecific: {
                  ...prev.ltSpecific,
                  participatingMembers: (prev.ltSpecific?.participatingMembers || []).map(m => {
                    if (m._id !== memberId) return m;
                    const prevIds = Array.isArray(m.cardIDs)
                      ? m.cardIDs
                      : m.cardID
                        ? [m.cardID]
                        : [];
                    const nextIds = Array.from(new Set([...(prevIds || []), cardID]));
                    return { ...m, cardIDs: nextIds, cardStatus: 'printed' };
                  }),
                },
              }));
              if (onBookingUpdated)
                onBookingUpdated({
                  ...localBooking,
                  ltSpecific: {
                    ...localBooking.ltSpecific,
                    participatingMembers: (localBooking.ltSpecific?.participatingMembers || []).map(
                      m => {
                        if (m._id !== memberId) return m;
                        const prevIds = Array.isArray(m.cardIDs)
                          ? m.cardIDs
                          : m.cardID
                            ? [m.cardID]
                            : [];
                        const nextIds = Array.from(new Set([...(prevIds || []), cardID]));
                        return { ...m, cardIDs: nextIds, cardStatus: 'printed' };
                      }
                    ),
                  },
                });
            }}
          />
        </Box>
      </Modal>
      {showCardNumbersFor && (
        <Dialog open={!!showCardNumbersFor} onClose={() => setShowCardNumbersFor(null)}>
          <DialogTitle>
            {(() => {
              const m = showCardNumbersFor;
              const primary = m?.cardNumber || '-';
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <span>Karteninformationen für</span>
                  <span>
                    <b style={{ fontSize: '1.6rem' }}>
                      {memberDisplayName(m)} - {primary}
                    </b>
                  </span>
                </Box>
              );
            })()}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              {(() => {
                const m = showCardNumbersFor;
                const ids = Array.isArray(m?.cardIDs) ? m.cardIDs : m?.cardID ? [m.cardID] : [];
                if (ids.length === 0) {
                  return (
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      Keine Kartennummern vorhanden
                    </Typography>
                  );
                }
                return ids.map((id, idx) => (
                  <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ width: 20 }}>{idx + 1}.</Typography>
                    <Chip
                      sx={{ px: 1 }}
                      icon={<CreditCardIcon fontSize='small' />}
                      label={id}
                      color='success'
                      variant='outlined'
                      size='small'
                    />
                  </Box>
                ));
              })()}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCardNumbersFor(null)}>Schließen</Button>
          </DialogActions>
        </Dialog>
      )}
      {reprintTarget && (
        <Dialog open={!!reprintTarget} onClose={() => setReprintTarget(null)}>
          <DialogTitle>Weitere Karte drucken?</DialogTitle>
          <DialogContent>
            <Typography>
              Bist du sicher, dass du eine weitere Karte für diesen Nutzer drucken möchtest? Bitte
              denke daran, bei einem durch den Spieler verschuldeten Verlust eine Karte über das
              Kassensystem abzurechnen.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReprintTarget(null)}>Abbrechen</Button>
            <Button
              variant='contained'
              onClick={async () => {
                try {
                  await updateMember({
                    memberId: reprintTarget._id,
                    cardStatus: 'reprint_requested',
                  });
                  setLocalBooking(prev => ({
                    ...prev,
                    ltSpecific: {
                      ...prev.ltSpecific,
                      participatingMembers: (prev.ltSpecific?.participatingMembers || []).map(m =>
                        m._id === reprintTarget._id ? { ...m, cardStatus: 'reprint_requested' } : m
                      ),
                    },
                  }));
                  if (onBookingUpdated)
                    onBookingUpdated({
                      ...localBooking,
                      ltSpecific: {
                        ...localBooking.ltSpecific,
                        participatingMembers: (
                          localBooking.ltSpecific?.participatingMembers || []
                        ).map(m =>
                          m._id === reprintTarget._id
                            ? { ...m, cardStatus: 'reprint_requested' }
                            : m
                        ),
                      },
                    });
                } catch (e) {
                  toast.error('Fehler beim erneuten Drucken');
                } finally {
                  setReprintTarget(null);
                }
              }}
            >
              Bestätigen
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {removeTarget && !isReadOnly && (
        <Dialog open={!!removeTarget} onClose={() => setRemoveTarget(null)}>
          <DialogTitle>Spieler entfernen?</DialogTitle>
          <DialogContent>
            <Typography>
              Bist du sicher, dass du {memberDisplayName(removeTarget)} aus dieser Buchung entfernen
              möchtest?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoveTarget(null)}>Abbrechen</Button>
            <Button
              variant='contained'
              color='error'
              onClick={async () => {
                try {
                  await handleRemoveMember(removeTarget);
                } finally {
                  setRemoveTarget(null);
                }
              }}
            >
              Entfernen
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <PrinterCleaningGuideModal
        open={cleaningDialogOpen}
        onClose={handleCloseCleaningDialog}
        printerName={printerStatus?.name}
        onConfirmStartCleaning={handleConfirmStartCleaning}
        disabled={printerStatus?.shouldStartCleaning === true}
        isCleaningActive={printerStatus?.shouldStartCleaning === true}
      />
      <Dialog open={cancelCleaningDialogOpen} onClose={handleCloseCancelCleaningDialog}>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color='error' />
          Reinigung abbrechen
        </DialogTitle>
        <DialogContent>
          <Alert severity='error' sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              WICHTIG: Neustart des Druckers erforderlich!
            </Typography>
            <Typography>
              Der Abbruch der Reinigung erfordert trotzdem einen Neustart des Druckers, damit der
              Drucker wieder ordnungsgemäß funktioniert.
            </Typography>
          </Alert>
          <Typography>
            Möchten Sie die Reinigung für <b>{printerStatus?.name || '-'}</b> wirklich abbrechen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelCleaningDialog}>Abbrechen</Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleConfirmCancelCleaning}
            autoFocus
          >
            Reinigung abbrechen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MemberCardQueueModal;
